// ContactMailView.tsx — die Mails eines Kontakts als EINE Tabelle.
//
// User 02.11.2026: „Nicht mehr trennen zwischen Posteingang und Archiv",
// „tabellarisch", „sortierbar und durchsuchbar", „Laden dauert zu lange".
//
// Bestand: Graph-Suche nach den Adressen des Kontakts in jedem lesbaren
// Postfach (alle Ordner außer Entwürfe/Junk/Papierkorb) plus die beim Kontakt
// gespeicherten Verknüpfungen — zusammengeführt und dedupliziert in
// `kommunikationTabelle.ts`, geladen schrittweise in `kontaktMailLaden.ts`.
// Die ersten Zeilen (Verknüpfungen, erstes Postfach) stehen da, bevor das
// letzte Postfach geantwortet hat; ein erneutes Öffnen zeigt sofort den
// letzten Stand und frischt im Hintergrund auf.
//
// Erhalten aus der alten Zweiteilung: Öffnen (Detail- bzw. Verknüpfungsfenster),
// Archivieren aus dem Detailfenster, Ziehen der Zeile in Vorgang/Dokumente,
// Vorgangs-Sprung, Büroklammer, Aktualisieren, Live-Aktualisierung, Zähler und
// Fehlerzustand für den Reiter, Firmen sehen die geschäftlichen Adressen ihrer
// Ansprechpartner mit.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Spinner, Tooltip } from "@fluentui/react-components";
import { Attach16Regular } from "@fluentui/react-icons";
import { graphMailService, type Email, type MailAttachment } from "../services/graphMailService";
import { openLinkedMailWindow } from "./mail/LinkedMailWindow";
import { openMailDetailWindow } from "./mail/MailDetailWindow";
import { hatFeinenZeiger, mailZeileZiehbar } from "./mail/mailZeileZiehen";
import { mailboxChipLabel } from "./mail/mailSearch";
import { useMailContext } from "../context/MailContext";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { useResponsive } from "../hooks/useResponsive";
import { useRealtime } from "../context/RealtimeContext";
import type { MailRealtimePayload } from "../services/realtimeService";
import { apiUrl } from "../utils/api";
import { canReadMailbox } from "../utils/mailboxAccess";
import { mailLinkService, type MailLinkForContactDto } from "../services/mailLinkService";
import { reportMailError } from "../services/mailError";
import { ladeKontaktKommunikation, type KommQuelle } from "./kontaktMailLaden";
import {
    ANHANG_MIT,
    ANHANG_OHNE,
    RICHTUNG_OPTIONEN,
    TREFFER_JE_POSTFACH,
    aktiveFilterAnzahl,
    anhangAnzahl,
    filtereKommunikation,
    filtereMailSpalten,
    mailFilterOptionen,
    ordnerLabel,
    type KommMailFilterWerte,
    formatZeit,
    fuehreKommunikationZusammen,
    ordnerNamen,
    sortiereKommunikation,
    teilnehmerAbfragen,
    type KommMailZeile,
    type KommSort,
    type PostfachTreffer,
} from "./kommunikationTabelle";
import { DataTable, type Column, type DateRangeValue } from "./redesign/table";
import { I } from "./redesign/kvpIcons";
import "./redesign/contactRedesign.css";
import "./kommunikation.css";

interface Props {
    contactId: number;
    isCompany?: boolean;
    /** E-Mail-Adresse(n) des Kontakts selbst. */
    emailAddresses: string[];
    /**
     * Gesamtzahl der Mails des Kontakts, sobald ALLE Quellen geantwortet haben —
     * damit der Kommunikations-Reiter eine leere Mail-Sektion ausblenden kann.
     */
    onCountChange?: (count: number) => void;
    /** Ist (mindestens) eine Quelle ausgefallen? Hält die Sektion sichtbar. */
    onErrorChange?: (hasError: boolean) => void;
    /**
     * Suchtext von außen (Werkzeugleiste des Kommunikations-Reiters). Ohne
     * Angabe bringt die Ansicht ihre eigene Leiste mit (alte Kontaktansicht).
     */
    suche?: string;
    /** Erhöhen = neu laden (Aktualisieren-Knopf des Reiters). */
    aktualisieren?: number;
}

/** So viele Zeilen erscheinen zunächst bzw. kommen je „Weitere anzeigen" dazu. */
const SCHRITT = 50;
/** Wie lange der letzte Stand eines Kontakts beim erneuten Öffnen sofort steht. */
const CACHE_MS = 5 * 60_000;

/**
 * Letzter Stand — nur im Speicher, nie im localStorage (Mailinhalte gehören
 * nicht auf die Platte des Browsers). Graph-Treffer je Kontakt + Adressen +
 * Postfächer, Verknüpfungen je Kontakt.
 */
const LETZTE_TREFFER = new Map<string, { wert: Record<string, Email[]>; at: number }>();
const LETZTE_LINKS = new Map<number, { wert: MailLinkForContactDto[]; at: number }>();
const frisch = (at: number) => Date.now() - at < CACHE_MS;

const normAdresse = (a: string | null | undefined) => String(a ?? "").trim().toLowerCase();

/**
 * Anhang-Namen je Nachricht — erst beim Überfahren der Büroklammer geholt (eine
 * Anfrage je angeschauter Zeile, nie für die ganze Liste) und im Speicher
 * gehalten.
 */
const ANHANG_NAMEN = new Map<string, Promise<string[]>>();
const ladeAnhangNamen = (mailbox: string, messageId: string): Promise<string[]> => {
    const key = `${normAdresse(mailbox)}::${messageId}`;
    let p = ANHANG_NAMEN.get(key);
    if (!p) {
        p = graphMailService
            .fetchAttachments(mailbox, messageId)
            .then((liste: MailAttachment[]) => liste.filter((a) => !a.isInline).map((a) => a.name || "(ohne Namen)"))
            .catch((e) => {
                ANHANG_NAMEN.delete(key);
                throw e;
            });
        ANHANG_NAMEN.set(key, p);
    }
    return p;
};

/** Spalte „Anhänge": Büroklammer + Anzahl, Namen im Tooltip. */
const AnhangZelle: React.FC<{ anzahl: number | null; mailbox: string | null; messageId: string }> = ({ anzahl, mailbox, messageId }) => {
    const [namen, setNamen] = useState<string[] | null>(null);
    const [fehler, setFehler] = useState(false);
    const laden = () => {
        if (!mailbox || namen || fehler) return;
        ladeAnhangNamen(mailbox, messageId).then(setNamen, () => setFehler(true));
    };
    const zahl = namen ? namen.length : anzahl;
    const inhalt = namen ? (
        namen.length ? (
            <div className="kkm-anhang-tip">
                {namen.map((n, i) => (
                    <div key={i}>{n}</div>
                ))}
            </div>
        ) : (
            "Nur eingebettete Bilder"
        )
    ) : mailbox && !fehler ? (
        "Anhänge werden geladen…"
    ) : zahl ? (
        `${zahl} ${zahl === 1 ? "Anhang" : "Anhänge"}`
    ) : (
        "Enthält Anhänge"
    );
    return (
        <Tooltip content={inhalt} relationship="description" withArrow onVisibleChange={(_, d) => d.visible && laden()}>
            <span className="kkm-anhang" role="img" aria-label={zahl ? `${zahl} ${zahl === 1 ? "Anhang" : "Anhänge"}` : "Enthält Anhänge"} onMouseEnter={laden}>
                <Attach16Regular />
                {zahl ? <span className="kkm-anhang-zahl">{zahl}</span> : null}
            </span>
        </Tooltip>
    );
};

export const ContactMailView: React.FC<Props> = ({
    contactId,
    isCompany,
    emailAddresses,
    onCountChange,
    onErrorChange,
    suche: sucheVonAussen,
    aktualisieren = 0,
}) => {
    const { mailboxes, currentMailbox, folderTrees } = useMailContext();
    const { openWindow } = useWindowManager();
    const navigate = useNavigate();
    const { isPhone } = useResponsive();
    const [feinerZeiger] = useState(hatFeinenZeiger);

    // ── Adressen ────────────────────────────────────────────────────────────
    // Aus dem INHALT abgeleitet, nicht aus der Array-Referenz — sonst lädt jede
    // Neuberechnung im Elternteil den Verlauf neu.
    const eigeneKey = (emailAddresses ?? []).map(normAdresse).filter(Boolean).sort().join(",");
    const eigene = useMemo(() => (eigeneKey ? eigeneKey.split(",") : []), [eigeneKey]);

    // A3/SP5: Firmen sehen zusätzlich die GESCHÄFTLICHEN Adressen ihrer
    // Ansprechpartner (Entscheidung 71); private AP-Adressen bleiben außen vor.
    // `null` = noch unterwegs — die Graph-Suche wartet darauf, damit sie nicht
    // erst mit den eigenen und gleich darauf mit allen Adressen läuft.
    const [apAdressen, setApAdressen] = useState<string[] | null>(isCompany ? null : []);
    useEffect(() => {
        if (!isCompany || !contactId) {
            setApAdressen([]);
            return;
        }
        let weg = false;
        setApAdressen(null);
        fetch(apiUrl(`contact-relation/mitarbeiter/${contactId}`))
            .then((res) => (res.ok ? res.json() : []))
            .then((mitarbeiter: any[]) => {
                const out: string[] = [];
                for (const ap of mitarbeiter || []) {
                    for (const c of ap?.communications || []) {
                        const geschaeftlich =
                            c?.kind === "geschaeftlich" ||
                            (c?.kind == null && (c?.label ?? "").toLowerCase() === "geschäftlich");
                        if (c?.type === "email" && c?.value && geschaeftlich) out.push(c.value);
                    }
                }
                if (!weg) setApAdressen(out);
            })
            .catch(() => {
                if (!weg) setApAdressen([]);
            });
        return () => {
            weg = true;
        };
    }, [contactId, isCompany]);

    const adressen = useMemo(
        () => (apAdressen === null ? null : Array.from(new Set([...eigene, ...apAdressen.map(normAdresse).filter(Boolean)]))),
        [eigene, apAdressen],
    );
    const adressKey = adressen ? adressen.slice().sort().join(",") : null;
    const abfragen = useMemo(() => (adressen ? teilnehmerAbfragen(adressen) : []), [adressen]);
    const kontaktAdressen = useMemo(() => new Set(adressen ?? eigene), [adressen, eigene]);

    // ── Postfächer: alle lesbaren, das aktuelle zuerst ──────────────────────
    const postfaecher = useMemo(() => {
        const lesbar = mailboxes.filter(canReadMailbox).map((m) => m.mailboxAddress);
        const liste = lesbar.length ? lesbar : currentMailbox ? [currentMailbox] : [];
        const aktuell = normAdresse(currentMailbox);
        return Array.from(new Set(liste)).sort((a, b) => Number(normAdresse(b) === aktuell) - Number(normAdresse(a) === aktuell));
    }, [mailboxes, currentMailbox]);
    const postfachKey = postfaecher.map(normAdresse).join(",");

    // ── Ladezustand ─────────────────────────────────────────────────────────
    const trefferKey = `${contactId}|${adressKey ?? "?"}|${postfachKey}`;
    const [treffer, setTreffer] = useState<Record<string, Email[]>>({});
    const [links, setLinks] = useState<MailLinkForContactDto[] | null>(null);
    const [offen, setOffen] = useState<Set<string>>(new Set());
    const [fehler, setFehler] = useState<KommQuelle[]>([]);
    const [versuch, setVersuch] = useState(0);

    const fehlerSetzen = useCallback((quelle: KommQuelle, error: unknown) => {
        reportMailError({
            area: "mail",
            action: quelle.art === "postfach" ? "search" : "load-linked-mails",
            error,
            fallbackMessage: "Der Mailverlauf konnte nicht vollständig geladen werden.",
        });
        setFehler((prev) => [...prev.filter((f) => JSON.stringify(f) !== JSON.stringify(quelle)), quelle]);
    }, []);
    const fehlerLoeschen = (pruef: (f: KommQuelle) => boolean) => setFehler((prev) => prev.filter((f) => !pruef(f)));

    // Letzten Stand sofort zeigen (stale-while-revalidate).
    useEffect(() => {
        const s = LETZTE_TREFFER.get(trefferKey);
        setTreffer(s && frisch(s.at) ? s.wert : {});
    }, [trefferKey]);
    useEffect(() => {
        const s = LETZTE_LINKS.get(contactId);
        setLinks(s && frisch(s.at) ? s.wert : null);
        setFehler([]);
    }, [contactId]);

    // (1) Verknüpfungen — unabhängig von Adressen und Postfächern, also sofort.
    useEffect(() => {
        if (!contactId) {
            setLinks([]);
            return;
        }
        let weg = false;
        fehlerLoeschen((f) => f.art === "verknuepfte");
        void ladeKontaktKommunikation<Email, MailLinkForContactDto>({
            postfaecher: [],
            abfragen: [],
            ladeVerknuepfte: () => mailLinkService.listByContact(contactId),
            suche: async () => [],
            onVerknuepfte: (l) => {
                setLinks(l);
                LETZTE_LINKS.set(contactId, { wert: l, at: Date.now() });
            },
            onPostfach: () => undefined,
            onFehler: (q, e) => {
                setLinks((prev) => prev ?? []);
                fehlerSetzen(q, e);
            },
            abgebrochen: () => weg,
        });
        return () => {
            weg = true;
        };
    }, [contactId, versuch, aktualisieren, fehlerSetzen]);

    // (2) Graph-Suche je Postfach — sobald die Adressen feststehen.
    const sucheIn = useCallback(
        (liste: string[], abgebrochen: () => boolean) => {
            if (!liste.length || !abfragen.length) {
                setOffen(new Set());
                return;
            }
            setOffen((prev) => new Set([...prev, ...liste]));
            fehlerLoeschen((f) => f.art === "postfach" && liste.includes(f.mailbox));
            const fertig = (mb: string) =>
                setOffen((prev) => {
                    const next = new Set(prev);
                    next.delete(mb);
                    return next;
                });
            void ladeKontaktKommunikation<Email, never>({
                postfaecher: liste,
                abfragen,
                ladeVerknuepfte: null,
                suche: (mb, kql) => graphMailService.searchMail(mb, kql, TREFFER_JE_POSTFACH),
                onVerknuepfte: () => undefined,
                onPostfach: (mb, mails) => {
                    setTreffer((prev) => ({ ...prev, [mb]: mails }));
                    fertig(mb);
                },
                onFehler: (q, e) => {
                    fehlerSetzen(q, e);
                    if (q.art === "postfach") fertig(q.mailbox);
                },
                abgebrochen,
            });
        },
        [abfragen, fehlerSetzen],
    );

    useEffect(() => {
        if (adressKey === null) return; // Ansprechpartner-Adressen noch unterwegs
        let weg = false;
        sucheIn(postfaecher, () => weg);
        return () => {
            weg = true;
            setOffen(new Set());
        };
        // `postfaecher`/`sucheIn` hängen am Inhalt (postfachKey/adressKey).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adressKey, postfachKey, versuch, aktualisieren]);

    const fertigGeladen = links !== null && adressKey !== null && offen.size === 0;

    // Graph-Stand merken, sobald alle Postfächer fehlerfrei geantwortet haben.
    useEffect(() => {
        if (adressKey !== null && offen.size === 0 && !fehler.some((f) => f.art === "postfach") && Object.keys(treffer).length > 0) {
            LETZTE_TREFFER.set(trefferKey, { wert: treffer, at: Date.now() });
        }
    }, [adressKey, offen.size, fehler, trefferKey, treffer]);

    // T5: Live-Aktualisierung — `mail`-Frames des AKTUELLEN Postfachs frischen
    // genau dieses Postfach auf (gebündelt, ein Posteingang bekommt Serien).
    const { subscribe: subscribeRealtime } = useRealtime();
    const sucheInRef = useRef(sucheIn);
    sucheInRef.current = sucheIn;
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const unsub = subscribeRealtime("mail", (evt) => {
            const p = evt.payload as MailRealtimePayload | undefined;
            const mb = p?.mailbox;
            if (!mb || !currentMailbox || normAdresse(mb) !== normAdresse(currentMailbox)) return;
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => sucheInRef.current([currentMailbox], () => false), 1500);
        });
        return () => {
            if (timer) clearTimeout(timer);
            unsub();
        };
    }, [subscribeRealtime, currentMailbox]);

    // ── Zeilen ──────────────────────────────────────────────────────────────
    const ordner = useMemo(() => ordnerNamen(folderTrees), [folderTrees]);
    const zeilen = useMemo(() => {
        const liste: PostfachTreffer[] = Object.entries(treffer).map(([mailbox, mails]) => ({ mailbox, mails }));
        return fuehreKommunikationZusammen({
            treffer: liste,
            links: links ?? [],
            kontaktAdressen,
            aktuellesPostfach: currentMailbox,
            ordner,
        });
    }, [treffer, links, kontaktAdressen, currentMailbox, ordner]);

    // Zähler/Fehler an den Reiter melden (stabil über Refs).
    const onCountRef = useRef(onCountChange);
    onCountRef.current = onCountChange;
    const onErrorRef = useRef(onErrorChange);
    onErrorRef.current = onErrorChange;
    useEffect(() => {
        if (fertigGeladen) onCountRef.current?.(zeilen.length);
    }, [fertigGeladen, zeilen.length]);
    useEffect(() => {
        onErrorRef.current?.(fehler.length > 0);
    }, [fehler.length]);

    // ── Suche, Sortierung, Seite ────────────────────────────────────────────
    const [eigeneSuche, setEigeneSuche] = useState("");
    const suche = sucheVonAussen ?? eigeneSuche;
    const [sort, setSort] = useState<KommSort>({ field: "datum", direction: "desc" });
    const [sichtbar, setSichtbar] = useState(SCHRITT);
    // Spaltenfilter (Lupen im Tabellenkopf) — je Kontakt neu.
    const [filterWerte, setFilterWerte] = useState<KommMailFilterWerte>({});
    const [datumFilter, setDatumFilter] = useState<DateRangeValue>({});
    useEffect(() => {
        setFilterWerte({});
        setDatumFilter({});
    }, [contactId]);
    useEffect(() => setSichtbar(SCHRITT), [suche, sort, contactId, filterWerte, datumFilter]);
    const filterAnzahl = aktiveFilterAnzahl(filterWerte, datumFilter);
    const filterOptionen = useMemo(() => mailFilterOptionen(zeilen), [zeilen]);
    const lesbarePostfaecher = useMemo(() => new Set(postfaecher.map(normAdresse)), [postfaecher]);

    const gefiltert = useMemo(
        () => filtereMailSpalten(filtereKommunikation(zeilen, suche), filterWerte, datumFilter),
        [zeilen, suche, filterWerte, datumFilter],
    );
    const sortiert = useMemo(() => sortiereKommunikation(gefiltert, sort), [gefiltert, sort]);
    const angezeigt = useMemo(() => sortiert.slice(0, sichtbar), [sortiert, sichtbar]);
    const gekappt = Object.values(treffer).some((m) => m.length >= TREFFER_JE_POSTFACH);

    // ── Öffnen ──────────────────────────────────────────────────────────────
    const neuLadenNachArchiv = useCallback((mailbox: string) => {
        // Graph vergibt beim Verschieben eine neue Id — die Zeile frisch holen.
        setTimeout(() => sucheInRef.current([mailbox], () => false), 800);
    }, []);

    const oeffnen = useCallback(
        (z: KommMailZeile) => {
            // Nur aus der Sicherung bekannt (oder die Verknüpfung zeigt genau
            // auf diese Nachricht) → Verknüpfungsfenster; es lädt Inhalt und
            // Anhänge selbst, auch aus fremden Postfächern (CBB).
            if (z.link && z.link.messageId === z.email.id) {
                openLinkedMailWindow(openWindow, z.link);
                return;
            }
            const mailbox = z.mailbox || currentMailbox;
            if (!mailbox) return;
            openMailDetailWindow(openWindow, {
                email: z.email,
                mailbox,
                initialMaximized: isPhone,
                onArchived: () => neuLadenNachArchiv(mailbox),
            });
        },
        [openWindow, currentMailbox, isPhone, neuLadenNachArchiv],
    );

    const zuVorgang = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        navigate(`/vorgaenge/${id}`);
    };

    // ── Spalten ─────────────────────────────────────────────────────────────
    const columns: Column<KommMailZeile>[] = useMemo(
        () => [
            {
                id: "datum",
                header: "Datum",
                sortable: true,
                width: 140,
                filter: { kind: "dateRange", rueckblick: true },
                cell: (z) => {
                    const { datum, uhrzeit } = formatZeit(z.zeit);
                    return (
                        <span className="kkm-datum">
                            {datum}
                            {uhrzeit && <small>{uhrzeit}</small>}
                        </span>
                    );
                },
            },
            {
                id: "richtung",
                header: "Richtung",
                sortable: true,
                width: 156,
                filter: { kind: "dropdown", options: [...RICHTUNG_OPTIONEN] },
                cell: (z) => (
                    <span className={`kkm-richtung ${z.richtung}`} title={z.richtung === "eingang" ? "Vom Kontakt erhalten" : "An den Kontakt gesendet"}>
                        <span style={{ display: "inline-flex", transform: z.richtung === "eingang" ? "scaleX(-1)" : undefined }}>{I.arrowR}</span>
                        {z.richtung === "eingang" ? "Eingang" : "Ausgang"}
                    </span>
                ),
            },
            {
                id: "von",
                header: "Von",
                sortable: true,
                hideable: true,
                // Feste Breite (Ellipse, voller Text im Tooltip): der Restplatz
                // geht an den Betreff.
                width: 200,
                filter: { kind: "text" },
                cell: (z) => (
                    <span className="kkm-person" title={z.von}>
                        {z.von || <span className="kkm-leer">—</span>}
                    </span>
                ),
            },
            {
                id: "an",
                header: "An",
                sortable: true,
                hideable: true,
                // Feste Breite (Ellipse, voller Text im Tooltip): der Restplatz
                // geht an den Betreff.
                width: 200,
                filter: { kind: "text" },
                cell: (z) => (
                    <span className="kkm-person" title={z.an}>
                        {z.an || <span className="kkm-leer">—</span>}
                    </span>
                ),
            },
            {
                id: "betreff",
                header: "Betreff",
                sortable: true,
                // Breiteste Spalte: nimmt den Restplatz (kt-wrap), nie unter 360 px —
                // lieber scrollt die Tabelle waagerecht, als den Betreff zu quetschen.
                minWidth: 380,
                className: "kt-wrap kkm-betreff-spalte",
                filter: { kind: "text" },
                cell: (z) => (
                    <div className="kkm-betreff">
                        <span
                            className="kkm-betreff-titel"
                            role="link"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    oeffnen(z);
                                }
                            }}
                        >
                            {z.email.subject || "(Kein Betreff)"}
                        </span>
                        {z.email.preview && <span className="kkm-vorschau" title={z.email.preview}>{z.email.preview}</span>}
                    </div>
                ),
            },
            {
                id: "anhaenge",
                label: "Anhänge",
                header: (
                    <span title="Anhänge" aria-label="Anhänge" style={{ display: "inline-flex" }}>
                        <Attach16Regular />
                    </span>
                ),
                sortable: true,
                hideable: true,
                width: 108,
                filter: { kind: "dropdown", options: [ANHANG_MIT, ANHANG_OHNE] },
                cell: (z) => {
                    const anzahl = anhangAnzahl(z);
                    if (anzahl === 0) return <span className="kkm-leer">—</span>;
                    const mailbox = z.mailbox || currentMailbox || null;
                    // Namen nur aus Postfächern, die der Nutzer lesen darf.
                    const ladbar = mailbox && lesbarePostfaecher.has(normAdresse(mailbox)) ? mailbox : null;
                    return <AnhangZelle anzahl={anzahl} mailbox={ladbar} messageId={z.email.id} />;
                },
            },
            {
                id: "ordner",
                header: "Postfach / Ordner",
                sortable: true,
                hideable: true,
                minWidth: 150,
                filter: { kind: "dropdown", options: filterOptionen.ordner },
                cell: (z) => (
                    <span className="kkm-ordner" title={z.mailbox ? `${ordnerLabel(z)} (${z.mailbox})` : ordnerLabel(z)}>
                        <span>{z.mailbox ? mailboxChipLabel(z.mailbox) : "—"}</span>
                        <small>{z.ordner ?? (z.link ? "Zugewiesen" : "")}</small>
                    </span>
                ),
            },
            {
                id: "vorgang",
                header: "Vorgang",
                sortable: true,
                hideable: true,
                minWidth: 150,
                filter: { kind: "dropdown", options: filterOptionen.vorgang },
                cell: (z) =>
                    z.vorgang ? (
                        <button
                            type="button"
                            className="kkm-vorgang"
                            title={`Zum Vorgang „${z.vorgang.titel}"`}
                            onClick={(e) => zuVorgang(e, z.vorgang!.id)}
                        >
                            {z.vorgang.titel}
                        </button>
                    ) : (
                        <span className="kkm-leer">—</span>
                    ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [oeffnen, filterOptionen, currentMailbox, lesbarePostfaecher],
    );
    // Die Tabelle scrollt waagerecht — auf dem Handy bleibt deshalb auch „An"
    // sichtbar; nur Postfach/Ordner tritt zurück (über das Spaltenmenü holbar).
    const [spaltenSicht, setSpaltenSicht] = useState<Record<string, boolean>>(() =>
        isPhone ? { ordner: false } : ({} as Record<string, boolean>),
    );

    // ── Darstellung ─────────────────────────────────────────────────────────
    const fehlerText = fehler
        .map((f) => (f.art === "verknuepfte" ? "zugewiesene Mails" : `Postfach ${mailboxChipLabel(f.mailbox)}`))
        .join(", ");
    const nochOffen = offen.size + (links === null ? 1 : 0) + (adressKey === null ? 1 : 0);
    const nichtsDa = zeilen.length === 0;

    const tabelle = (
        <>
            {fehler.length > 0 && (
                <div className="kkm-hinweis fehler" role="alert">
                    Nicht vollständig geladen: {fehlerText}.
                    <button type="button" className="tbtn" onClick={() => setVersuch((v) => v + 1)}>
                        Erneut versuchen
                    </button>
                </div>
            )}
            {adressKey !== null && abfragen.length === 0 && (
                <div className="kkm-hinweis">Für diesen Kontakt ist keine E-Mail-Adresse hinterlegt — gezeigt werden nur zugewiesene Mails.</div>
            )}
            {abfragen.length > 0 && postfaecher.length === 0 && (
                <div className="kkm-hinweis">Kein lesbares Postfach — gezeigt werden nur zugewiesene Mails.</div>
            )}
            {filterAnzahl > 0 && (
                <div className="kkm-hinweis kkm-filterhinweis">
                    {filterAnzahl === 1 ? "1 Spaltenfilter aktiv" : `${filterAnzahl} Spaltenfilter aktiv`} — {gefiltert.length} von {zeilen.length} Mails.
                    <button
                        type="button"
                        className="tbtn"
                        onClick={() => {
                            setFilterWerte({});
                            setDatumFilter({});
                        }}
                    >
                        Filter zurücksetzen
                    </button>
                </div>
            )}
            {nichtsDa && nochOffen > 0 ? (
                <div className="card">
                    <div className="kkm-skelett" aria-busy="true" aria-label="E-Mails werden geladen">
                        <span style={{ width: "92%" }} />
                        <span style={{ width: "78%" }} />
                        <span style={{ width: "85%" }} />
                    </div>
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    rows={angezeigt}
                    rowKey={(z) => z.key}
                    onRowClick={(z) => oeffnen(z)}
                    rowClassName={(z) => (z.email.read === false ? "kkm-ungelesen" : undefined)}
                    // User 01.11.2026: Mail aus der Liste in einen Vorgang oder die Dokumente ziehen.
                    rowDraggable={(z) => feinerZeiger && Boolean(z.mailbox || currentMailbox)}
                    onRowDragStart={(z, _i, e) =>
                        mailZeileZiehbar(z.mailbox || currentMailbox, z.email, feinerZeiger).onDragStart?.(e as React.DragEvent<HTMLElement>)
                    }
                    sort={sort}
                    onSortChange={(next) => setSort(next ?? { field: "datum", direction: "desc" })}
                    filterValues={filterWerte}
                    onFilterChange={(id, v) => setFilterWerte((prev) => ({ ...prev, [id]: v }))}
                    dateRangeValues={{ datum: datumFilter }}
                    onDateRangeChange={(_id, next) => setDatumFilter(next)}
                    columnVisibility={spaltenSicht}
                    onColumnVisibilityChange={setSpaltenSicht}
                    showColumnMenu
                    // Waagerecht scrollbar statt gequetscht (schmales Fenster, Handy).
                    cardClassName="kkm-karte"
                    tableClassName="kt-dense kkm-tabelle kkm-tabelle-mails"
                    emptyText={
                        filterAnzahl > 0
                            ? "Keine Mail passt zu den Filtern."
                            : suche.trim()
                              ? "Keine Mail passt zur Suche."
                              : "Zu diesem Kontakt gibt es keine Mails."
                    }
                />
            )}
            {sortiert.length > angezeigt.length && (
                <div className="kkm-mehr">
                    <button type="button" className="tbtn" onClick={() => setSichtbar((n) => n + SCHRITT)}>
                        Weitere {Math.min(SCHRITT, sortiert.length - angezeigt.length)} anzeigen ({sortiert.length - angezeigt.length} übrig)
                    </button>
                </div>
            )}
            {gekappt && (
                <div className="kkm-hinweis">
                    Je Postfach stehen hier die neuesten {TREFFER_JE_POSTFACH} Mails des Kontakts. Ältere findet die Suche im Mail-Bereich.
                </div>
            )}
        </>
    );

    const ladeStatus =
        nochOffen > 0 ? (
            <span className="kkm-status" aria-live="polite">
                <Spinner size="extra-tiny" />
                {nichtsDa ? "Wird geladen…" : "Weitere Postfächer werden durchsucht…"}
            </span>
        ) : null;

    // Eingebettet (Kommunikations-Reiter): der Reiter hält Leiste und Rahmen.
    if (sucheVonAussen !== undefined) {
        return (
            <div className="kkm-mails">
                {ladeStatus && <div style={{ marginBottom: 8 }}>{ladeStatus}</div>}
                {tabelle}
            </div>
        );
    }

    return (
        <div className="kvp-rd">
            <div className="kkm-wrap">
                <div className="kkm-leiste">
                    <label className="search">
                        {I.search}
                        <input
                            type="search"
                            value={eigeneSuche}
                            onChange={(e) => setEigeneSuche(e.target.value)}
                            placeholder="Betreff, Absender, Empfänger, Vorschau …"
                            aria-label="Mails durchsuchen"
                        />
                    </label>
                    <span className="kkm-leiste-rechts">
                        {ladeStatus}
                        <span className="kkm-zahl" title="Mails">{gefiltert.length}</span>
                        <button type="button" className="tbtn" onClick={() => setVersuch((v) => v + 1)} title="Aktualisieren">
                            ↻
                        </button>
                    </span>
                </div>
                {tabelle}
            </div>
        </div>
    );
};

export default ContactMailView;
